import { SerializedStyles, css } from '@emotion/react';
import { pxToRem, fontSizeLineHeight, transition, mediaQueries, weights } from '../utils/css-utils';
import { GatsbyImage, IGatsbyImageData } from "gatsby-plugin-image";
import React from 'react';
import { setImageSize } from '../utils/image-utils';
import Heading from './Heading';
import { dropShadows, focusStyles } from '../utils/global-styles';
import { colors } from '../utils/colors';
import { Link } from 'gatsby';

type CardProps = {
  title: string,
  href: string,
  description?: string | null,
  linkText?: string | null,
  date: string | null,
  author: string | null,
  image?: IGatsbyImageData,
  altText: string,
  tag?: string,
  css?: SerializedStyles
};

const Card:React.FC<CardProps> = ({ title, href, description, date, author, image, linkText, altText, tag, ...props }) => {

  // Certain fields are only valid to display if there is or isn't an image
  if (image) {
    description = null;
    linkText = null;
  }
  else {
    author = null;
    date = null;
  }
  
  const linkStyles = css`
    display: flex;
    flex-direction: column;
    text-decoration: none;
    background: ${colors.black900};
    border-radius: ${pxToRem(16)};
    height: 100%;
    box-shadow: 0 0 0 ${pxToRem(2)} transparent;
    transition: ${transition()};

    &:hover,
    &:focus {
      box-shadow: 0 0 0 ${pxToRem(2)} ${colors.yellow600};
    }
    
    &:focus-visible {
      ${focusStyles}
    }
  `;

  const textWrapperStyles = css`
    display: flex;
    flex-direction: column;
    padding: ${ image ? `${pxToRem(24)} ${pxToRem(16)}` : `${pxToRem(32)} ${pxToRem(24)}`};
    flex: 0 1 100%;
  
    ${mediaQueries.md} {
      padding: ${ image ? `${pxToRem(32)} ${pxToRem(24)}` : `${pxToRem(40)} ${pxToRem(32)}`};
    }
  `;
  
  const catStyles = css`
    ${fontSizeLineHeight(13, 20)}
    font-weight: ${weights.bold};
    margin: 0 0 ${pxToRem(8)};
    color: #899296;
    text-transform: uppercase;
    font-family: "Space Grotesk", sans-serif;

    ${mediaQueries.lg} {
       margin-bottom: ${pxToRem(16)};
    }
  `;

  const titleStyles = css`
    margin-bottom: ${image ? `${pxToRem(24)}` : `${pxToRem(8)}`};
    
    ${mediaQueries.md} {
      margin-bottom: ${image ? `${pxToRem(32)}` : `${pxToRem(8)}`};
    }
    
    &:last-child {
      margin-bottom: 0;
    }
  `;

  const descriptionStyles = css`
    margin-bottom: ${pxToRem(32)};
    line-height: 1.68; 
    color: ${colors.white};
    
    ${mediaQueries.md} {
      margin-bottom: ${pxToRem(40)};
    }
    
    &:last-child {
      margin-bottom: 0;
    }
  `;

  const authorStyles = css`
    ${fontSizeLineHeight(13, 20)}
    margin-top: auto;
    margin-bottom: ${pxToRem(4)};
    font-weight: ${weights.medium};
    color: ${colors.white};
    font-family: "Space Grotesk", sans-serif;
  `;
  
  const dateStyles = css`
    ${fontSizeLineHeight(13, 20)}
    margin-top: ${author ? '0' : 'auto'};
    margin-bottom: 0;
    color: #899296;
    font-weight: ${weights.medium};
    font-family: "Space Grotesk", sans-serif;
  `;
  
  const buttonStyles = css`
    align-self: flex-start;
    margin-top: auto;
  `;

  const imgStyles = css`
    border-top-left-radius: ${pxToRem(16)};
    border-top-right-radius: ${pxToRem(16)};
    flex: 0 0 auto;
  `;

  return (
    <Link to={href} css={linkStyles} {...props}>

      {image ? <GatsbyImage image={setImageSize(image, 'card') as IGatsbyImageData} alt={altText || ""} css={imgStyles}/> : ''}

      <div css={textWrapperStyles}>
      
        {tag && <p css={catStyles}>{tag}</p>}

        <Heading level={3} levelStyle={image ? 5 : 4} css={titleStyles} text={title} />

        {description && <p css={descriptionStyles} dangerouslySetInnerHTML={{__html: description}}/>}

        {author && <p css={authorStyles}>{author}</p>}
          
        {date && <p css={dateStyles}>{date}</p>}

        {/* {linkText && <Button text={linkText} type="faux" level="tertiary" css={buttonStyles} />} */}
      </div>
    </Link>
  );
};

export { Card, CardProps };
